/**
 * A world written by an earlier build (a suspend save from before round two) lacks the fields
 * added since: gear, smoke, searchlights, the executioners' roster, eleven `SimMods`. Left
 * undefined they turn into NaN cooldowns and crashes, so `restoreSim` gives each its neutral
 * value. A current world is left exactly as it is - same keys, same order, same hash.
 */

import { defaultMods } from './mods';
import type { World } from './types';

/** Set every key of `defaults` that `target` does not have yet; never touch one it has. */
function fill<T extends object>(target: T, defaults: Partial<T>): void {
  for (const key of Object.keys(defaults) as Array<keyof T>) {
    if (target[key] === undefined) target[key] = defaults[key] as T[keyof T];
  }
}

export function upgradeWorld(world: World): void {
  fill(world, { smokes: [] });
  fill(world.mods, defaultMods());
  fill(world.ranger, {
    gear: { smoke: 0, pistolRounds: 0, nvg: false, wireCutters: false },
    hurtBy: 'none', burstRounds: 0, raiseTicks: 0,
    // Round five: the carbine's sub-tick carry. Zero is the rate an old save was fired at.
    fireCarry: 0,
  });
  // Searchlights are left dark rather than conjured mid-mission: the old save never had them lit.
  fill(world.hazards, { searchlights: [], litTicks: 0, slideX: 0, slideZ: 0 });
  // Round five: footprints. A ranger restored from an older save starts with clean snow behind him.
  fill(world.hazards, { tracks: [] });
  fill(world.objective, { tagsRecovered: false, executioners: [] });
  // Round three (10 §3). A save from before it is of one of the twelve originals, which use none of this:
  // no timed events, no quest item, no blackout groups - so the neutral values are also the right ones.
  fill(world.objective, {
    usedPropIds: [], usingPropId: -1, plantedPropIds: [], zoneTicks: [], zonesDone: [], holdTicks: 0,
    targetEnemyId: -1, targetFleeing: false, targetGone: false, targetLooted: false, item: 'none',
    armedTick: -1, completeTick: -1, timed: [], windowOpen: false, blackouts: [], shootOrdered: false,
    chargesFired: 0, zoneEnteredTick: -1,
  });
  // Round four: vehicles and the falling bridge. Neither existed in an older save, so both are neutral.
  fill(world.objective, { spanDeed: false, targetAboardId: -1 });
  for (const e of world.enemies) fill(e, { salvaged: false, selfDestroyed: false });
}
